/**
 * Public cookie banner isolation.
 *
 * Page CSS (entry custom CSS, layout CSS, `button` / `a` / `p` / `svg` / `*`
 * rules, and `body { font-size !important }`) is in the light DOM. A closed
 * shadow tree is the strongest isolation that still lets empty space pass
 * clicks through to the page. An iframe cannot: the frame element is one hit
 * target, so a full-viewport frame blocks the page and a button-sized frame
 * cannot hold the preferences dialog.
 *
 * Only Next.js stylesheets are brought into the shadow tree, so Tailwind
 * utilities on the banner still apply. Tenant `<style>` tags are not.
 * Theme colours still follow the page because custom properties inherit from
 * `html` / `body` and are mirrored onto the mount node.
 */

export const COOKIE_CONSENT_HOST_ATTR = "data-cms-cookie-consent";

const PUBLIC_Z_INDEX = "2147483647";

const HOST_LOCK_DECLARATIONS = [
	"display: block",
	"box-sizing: border-box",
	"margin: 0",
	"padding: 0",
	"border: 0",
	"background: transparent",
	"background-color: transparent",
	"box-shadow: none",
	"outline: none",
	"transform: none",
	"filter: none",
	"backdrop-filter: none",
	"opacity: 1",
	"visibility: visible",
	"overflow: visible",
	"pointer-events: none",
	"float: none",
	"clear: none",
	"clip-path: none",
	"contain: none",
	"content-visibility: visible",
	"zoom: 1",
	"max-width: none",
	"max-height: none",
	"min-width: 0",
	"min-height: 0",
	"font-family: var(--font-sans), ui-sans-serif, system-ui, sans-serif",
	"font-size: 16px",
	"font-weight: 400",
	"font-style: normal",
	"line-height: 1.5",
	"letter-spacing: normal",
	"word-spacing: normal",
	"text-transform: none",
	"text-align: start",
	"text-indent: 0",
	"text-decoration: none",
	"text-shadow: none",
	"color: var(--foreground)",
	"direction: ltr",
	"writing-mode: horizontal-tb",
	"white-space: normal",
	"cursor: auto",
	"isolation: auto",
	"mix-blend-mode: normal",
	"mask: none",
	"perspective: none",
	"rotate: none",
	"scale: none",
	"translate: none",
	"animation: none",
	"transition: none",
] as const;

const DENY_ATTRIBUTES = [
	"data-tenant-theme",
	"data-layout-structure-responsive",
	"data-global-scripts-slot",
	"data-entry-id",
] as const;

export type StylesheetOwnerSnapshot = {
	tagName: string;
	rel?: string | null;
	href?: string | null;
	attributes?: Readonly<Record<string, string | null>>;
};

export function buildCookieHostCss(previewMode: boolean): string {
	const position = previewMode ? "absolute" : "fixed";
	const zIndex = previewMode ? "40" : PUBLIC_Z_INDEX;
	const declarations = [
		...HOST_LOCK_DECLARATIONS,
		`position: ${position}`,
		"inset: 0",
		`z-index: ${zIndex}`,
	];
	return declarations
		.map((declaration) => `${declaration} !important`)
		.join("; ");
}

function hasDeniedAttribute(
	attributes: Readonly<Record<string, string | null>>
): boolean {
	for (const name of DENY_ATTRIBUTES) {
		if (attributes[name] != null) {
			return true;
		}
	}
	const slot = attributes["data-slot"];
	return typeof slot === "string" && slot.startsWith("page-custom-css");
}

function isNextAssetUrl(
	value: string | null | undefined,
	pageOrigin: string
): boolean {
	if (!value) {
		return false;
	}
	try {
		const url = new URL(value, pageOrigin);
		if (url.origin !== pageOrigin) {
			return false;
		}
		return url.pathname.includes("/_next/");
	} catch {
		return false;
	}
}

/** True only for this app's compiled CSS, never tenant or entry styles. */
export function isFrameworkStylesheetOwner(
	owner: StylesheetOwnerSnapshot,
	pageOrigin: string
): boolean {
	const attributes = owner.attributes ?? {};
	if (hasDeniedAttribute(attributes)) {
		return false;
	}
	const tag = owner.tagName.toLowerCase();
	if (tag === "link") {
		if ((owner.rel ?? "").toLowerCase() !== "stylesheet") {
			return false;
		}
		return isNextAssetUrl(owner.href, pageOrigin);
	}
	if (tag === "style") {
		return isNextAssetUrl(attributes["data-href"], pageOrigin);
	}
	return false;
}

export function frameworkStylesheetKey(owner: StylesheetOwnerSnapshot): string {
	if (owner.tagName.toLowerCase() === "link") {
		return `link:${owner.href ?? ""}`;
	}
	return `style:${owner.attributes?.["data-href"] ?? ""}`;
}

function snapshotStylesheetOwner(node: Element): StylesheetOwnerSnapshot {
	const attributes: Record<string, string | null> = {};
	for (const name of node.getAttributeNames()) {
		attributes[name] = node.getAttribute(name);
	}
	if (node instanceof HTMLLinkElement) {
		return {
			tagName: "link",
			rel: node.rel,
			href: node.href,
			attributes,
		};
	}
	return { tagName: node.tagName, attributes };
}

function findOwningSheet(node: Element): CSSStyleSheet | null {
	for (const sheet of document.styleSheets) {
		if (sheet.ownerNode === node) {
			return sheet;
		}
	}
	return null;
}

function canReadSheet(sheet: CSSStyleSheet): boolean {
	try {
		return sheet.cssRules.length >= 0;
	} catch {
		return false;
	}
}

function removeStaleClones(shadow: ShadowRoot, wanted: ReadonlySet<string>) {
	for (const existing of shadow.querySelectorAll(
		"[data-cms-cookie-cloned-style]"
	)) {
		const key = existing.getAttribute("data-cms-cookie-cloned-style");
		if (!(key && wanted.has(key))) {
			existing.remove();
		}
	}
}

/** Copy framework CSS into the shadow tree. Tenant style tags stay outside. */
export function syncFrameworkStyles(shadow: ShadowRoot): void {
	const origin = window.location.origin;
	const adoptable: CSSStyleSheet[] = [];
	const cloneTargets: { key: string; node: Element }[] = [];

	for (const node of document.querySelectorAll(
		"link[rel='stylesheet'], style"
	)) {
		if (
			!(node instanceof HTMLLinkElement || node instanceof HTMLStyleElement)
		) {
			continue;
		}
		const snapshot = snapshotStylesheetOwner(node);
		if (!isFrameworkStylesheetOwner(snapshot, origin)) {
			continue;
		}
		const key = frameworkStylesheetKey(snapshot);
		const sheet = findOwningSheet(node);
		if (sheet && canReadSheet(sheet)) {
			adoptable.push(sheet);
			continue;
		}
		cloneTargets.push({ key, node });
	}

	try {
		shadow.adoptedStyleSheets = adoptable;
	} catch {
		for (const sheet of adoptable) {
			const owner = sheet.ownerNode;
			if (owner instanceof Element) {
				const snapshot = snapshotStylesheetOwner(owner);
				cloneTargets.push({
					key: frameworkStylesheetKey(snapshot),
					node: owner,
				});
			}
		}
	}

	const wanted = new Set(cloneTargets.map((target) => target.key));
	removeStaleClones(shadow, wanted);
	const present = new Set(
		[...shadow.querySelectorAll("[data-cms-cookie-cloned-style]")].map((node) =>
			node.getAttribute("data-cms-cookie-cloned-style")
		)
	);
	for (const target of cloneTargets) {
		if (present.has(target.key)) {
			continue;
		}
		const clone = target.node.cloneNode(true);
		if (!(clone instanceof Element)) {
			continue;
		}
		clone.setAttribute("data-cms-cookie-cloned-style", target.key);
		shadow.appendChild(clone);
		present.add(target.key);
	}
}

/** Mirror theme tokens into the shadow mount so `.dark` utilities resolve there. */
export function syncCookieMountTheme(mount: HTMLElement): void {
	const themeRoot = document.documentElement;
	const tokenSource = document.body ?? themeRoot;
	mount.classList.toggle("dark", themeRoot.classList.contains("dark"));
	const computed = getComputedStyle(tokenSource);
	mount.style.colorScheme = computed.colorScheme;
	for (let index = 0; index < computed.length; index += 1) {
		const name = computed.item(index);
		if (!name.startsWith("--")) {
			continue;
		}
		mount.style.setProperty(name, computed.getPropertyValue(name));
	}
}
